/**
 * Splitting a Windows command line, so the submit form can show an analyst what
 * their arguments will actually become.
 *
 * The server does the authoritative split (voltz-core/launch) and stores the
 * result; this is the same grammar, run locally, so the preview updates as the
 * analyst types instead of after they have spent a run finding out. Three
 * implementations of one grammar — Go's, this, and the agent's inverse quoter —
 * are pinned to one shared corpus, `src/core/launch/argv_cases.json`, which
 * launchArgs.test.ts reads directly. A change to the rules belongs there.
 *
 * The rules are CommandLineToArgvW's, for every argument AFTER the first:
 * space and tab separate; a quoted run may sit anywhere inside an argument;
 * backslashes are literal unless they immediately precede a quote, where 2n
 * yield n and the quote delimits, and 2n+1 yield n and a literal quote; and
 * inside a quoted run `""` yields one quote and stays quoted. An unterminated
 * quote runs to the end, which is what Windows does rather than an error.
 */

const SEPARATORS = new Set([' ', '\t']);

/** Splits a command-line tail into the arguments a Windows process receives. */
export function parseArgs(raw: string): string[] {
  const args: string[] = [];
  let i = 0;
  while (i < raw.length) {
    while (i < raw.length && SEPARATORS.has(raw[i])) i += 1;
    if (i >= raw.length) break;
    const [arg, next] = parseOne(raw, i);
    args.push(arg);
    i = next;
  }
  return args;
}

/** Reads the one argument beginning at `start`, with the index just past it. */
function parseOne(raw: string, start: number): [string, number] {
  let out = '';
  let inQuotes = false;
  let i = start;
  while (i < raw.length) {
    const c = raw[i];
    if (c === '\\') {
      // Count the whole run before looking at what follows it: only a run that
      // ends at a quote is an escape, and then only half of it survives.
      const runStart = i;
      while (i < raw.length && raw[i] === '\\') i += 1;
      const n = i - runStart;
      if (i < raw.length && raw[i] === '"') {
        out += '\\'.repeat(Math.floor(n / 2));
        if (n % 2 === 1) {
          // The odd backslash escapes the quote, which becomes content. An
          // even run leaves the quote to the branch below, where it delimits.
          out += '"';
          i += 1;
        }
        continue;
      }
      out += '\\'.repeat(n);
      continue;
    }
    if (c === '"') {
      if (inQuotes && raw[i + 1] === '"') {
        out += '"';
        i += 2;
        continue;
      }
      inQuotes = !inQuotes;
      i += 1;
      continue;
    }
    if (!inQuotes && SEPARATORS.has(c)) return [out, i];
    out += c;
    i += 1;
  }
  return [out, i];
}

/**
 * The set an argument may contain and still be passed bare. Matches the agent's
 * own table, which is wider than strictly necessary: a staged sample path also
 * reaches cmd.exe, msiexec and PowerShell, whose parsers treat characters that
 * are legal in a filename as syntax.
 */
const UNQUOTED = /^[A-Za-z0-9._\-\\:/]+$/;

/** Quotes one argument so Windows reads it back unchanged. */
export function quoteArg(arg: string): string {
  if (arg !== '' && UNQUOTED.test(arg)) return arg;
  let out = '"';
  let i = 0;
  while (i < arg.length) {
    const runStart = i;
    while (i < arg.length && arg[i] === '\\') i += 1;
    const n = i - runStart;
    if (i === arg.length) {
      // Trailing backslashes are doubled so the closing quote cannot be read
      // as escaped by the last one.
      out += '\\'.repeat(n * 2);
      break;
    }
    if (arg[i] === '"') {
      out += `${'\\'.repeat(n * 2 + 1)}"`;
    } else {
      out += '\\'.repeat(n) + arg[i];
    }
    i += 1;
  }
  return `${out}"`;
}

/**
 * The command line an executable will be started with, for the preview.
 *
 * Built from the PARSED arguments rather than echoing what was typed, because
 * that is what the guest does: it re-quotes each argument, and seeing the
 * result is the whole point of showing it.
 */
export function previewExeCommandLine(name: string, raw: string): string {
  const args = parseArgs(raw).map(quoteArg);
  return [quoteArg(name), ...args].join(' ');
}

/**
 * The command line a DLL will be called through.
 *
 * The arguments are NOT split: a trampoline hands everything after the export
 * to it as a single string, so showing them split would describe a command line
 * that never exists.
 */
export function previewDllCommandLine(name: string, exportName: string, raw: string): string {
  const target = exportName.trim() === '' ? '<chosen from the DLL>' : exportName.trim();
  const tail = raw.trim() === '' ? '' : ` ${raw.trim()}`;
  return `rundll32.exe "${name}",${target}${tail}`;
}

/**
 * The one-line "launched as" a report shows, or null when the run took the
 * sandbox's defaults.
 *
 * Null rather than a sentence about defaults: nearly every run uses them, and a
 * row on all of them would be noise around the one case worth reading.
 */
export function launchedAsLine(
  name: string,
  options: {
    exe?: { args?: string; args_parsed?: string[] };
    dll?: { export?: string; args?: string };
  } | null
): string | null {
  if (!options) return null;
  if (options.exe) {
    const args = options.exe.args ?? '';
    return args.trim() === '' ? null : previewExeCommandLine(name, args);
  }
  if (options.dll) {
    const { export: exportName = '', args = '' } = options.dll;
    if (exportName.trim() === '' && args.trim() === '') return null;
    return previewDllCommandLine(name, exportName, args);
  }
  return null;
}
